import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { HandHeart, Calendar, Clock, User, Ban, Hand } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr.replace(/-/g, '/'));
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const formatTime = (timeStr) => {
  if (!timeStr) return '';
  const [hours, minutes] = timeStr.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
};

// A shift is "past" once its start time has elapsed (user local time).
const isShiftPast = (schedule) => {
  if (!schedule?.date) return false;
  const [h, m] = (schedule.start_time || '00:00').split(':').map(Number);
  const start = new Date(schedule.date.replace(/-/g, '/') + ' 00:00:00');
  start.setHours(Number.isFinite(h) ? h : 0, Number.isFinite(m) ? m : 0, 0, 0);
  return start.getTime() <= Date.now();
};

const ShiftLine = ({ schedule }) => {
  if (!schedule) return <span className="text-gray-400 text-sm">Shift unavailable</span>;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-700">
      <span className="inline-flex items-center gap-1.5 font-medium text-gray-900">
        <Calendar className="h-4 w-4 text-teal-600" />
        {formatDate(schedule.date)}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <Clock className="h-4 w-4 text-gray-400" />
        {formatTime(schedule.start_time)} – {formatTime(schedule.end_time)}
      </span>
      {schedule.role && <Badge variant="outline" className="capitalize">{String(schedule.role).replace(/_/g, ' ')}</Badge>}
      {schedule.shift_type === 'on_call' && <Badge className="bg-purple-600">On-Call</Badge>}
    </div>
  );
};

const OpenShifts = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const [openOffers, setOpenOffers] = useState([]);
  const [myOffers, setMyOffers] = useState([]);
  const [mySchedules, setMySchedules] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showOfferModal, setShowOfferModal] = useState(false);
  const [selectedScheduleId, setSelectedScheduleId] = useState('');
  const [offerReason, setOfferReason] = useState('');

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchAll = useCallback(async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const headers = authHeaders();
      const [openRes, allRes] = await Promise.all([
        axios.get(`${API}/shift-offers?status=open`, { headers }),
        axios.get(`${API}/shift-offers?status=all`, { headers }),
      ]);
      setOpenOffers((openRes.data || []).filter((o) => !isShiftPast(o.schedule)));

      const all = allRes.data || [];
      setMyOffers(
        all.filter((o) => o.offering_user_id === user.id || o.claiming_user_id === user.id)
      );
    } catch (e) {
      console.error('Failed to load shift offers:', e);
    }
  }, [user.id]);

  const fetchMySchedules = useCallback(async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await axios.get(`${API}/schedules?user_id=${user.id}&date_from=${today}`, { headers: authHeaders() });
      const mine = (res.data || []).filter((s) => s.user_id === user.id && s.shift_type !== 'pto' && !isShiftPast(s));
      mine.sort((a, b) => {
        const dc = new Date(a.date.replace(/-/g, '/')) - new Date(b.date.replace(/-/g, '/'));
        return dc !== 0 ? dc : (a.start_time || '').localeCompare(b.start_time || '');
      });
      setMySchedules(mine);
    } catch (e) {
      console.error('Failed to load my schedules:', e);
    }
  }, [user.id]);

  useEffect(() => {
    fetchAll();
    fetchMySchedules();
  }, [fetchAll, fetchMySchedules]);

  // IDs of shifts that already have an active offer (so we don't list them again)
  const offeredScheduleIds = new Set(myOffers.filter((o) => ['open', 'claimed'].includes(o.status)).map((o) => o.schedule_id));
  const offerableSchedules = mySchedules.filter((s) => !offeredScheduleIds.has(s.id));

  const handleCreateOffer = async () => {
    if (!selectedScheduleId) {
      toast.error('Please select a shift to offer up');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/shift-offers`, { schedule_id: selectedScheduleId, reason: offerReason || null }, { headers: authHeaders() });
      toast.success('Shift offered up — teammates with your role can now claim it');
      setShowOfferModal(false);
      setSelectedScheduleId('');
      setOfferReason('');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to offer up shift');
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async (offerId) => {
    setLoading(true);
    try {
      await axios.post(`${API}/shift-offers/${offerId}/claim`, {}, { headers: authHeaders() });
      toast.success('Shift claimed! A manager needs to approve the transfer.');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to claim shift');
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (offerId) => {
    setLoading(true);
    try {
      await axios.delete(`${API}/shift-offers/${offerId}`, { headers: authHeaders() });
      toast.success('Shift offer withdrawn');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to withdraw');
    } finally {
      setLoading(false);
    }
  };

  const statusBadge = (status) => {
    const map = {
      open: 'bg-teal-600',
      claimed: 'bg-amber-600',
      completed: 'bg-green-600',
      denied: 'bg-red-600',
      cancelled: 'bg-gray-500',
      expired: 'bg-gray-400',
    };
    const labels = { open: 'Open', claimed: 'Awaiting Approval', completed: 'Picked Up', denied: 'Declined', cancelled: 'Withdrawn', expired: 'Expired' };
    return <Badge className={map[status] || 'bg-gray-500'}>{labels[status] || status}</Badge>;
  };

  return (
    <div data-testid="open-shifts-tab">
      <div className="flex items-center justify-end mb-4">
        <Button onClick={() => setShowOfferModal(true)} className="bg-teal-600 hover:bg-teal-700" data-testid="offer-shift-btn">
          <HandHeart className="h-4 w-4 mr-2" />
          Offer Up a Shift
        </Button>
      </div>

      {/* Open shift board */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Hand className="h-5 w-5 text-teal-600" />
            <span>Open Shifts to Claim</span>
            <Badge className="bg-teal-600">{openOffers.length}</Badge>
          </CardTitle>
          <CardDescription>Shifts your teammates have offered up. Claim one to pick up the coverage (manager approval required).</CardDescription>
        </CardHeader>
        <CardContent>
          {openOffers.length === 0 ? (
            <div className="text-center py-12 text-gray-500" data-testid="no-open-shifts">
              <Hand className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No open shifts right now</p>
            </div>
          ) : (
            <div className="space-y-3">
              {openOffers.map((offer) => {
                const isOwn = offer.offering_user_id === user.id;
                const roleMatch = offer.schedule?.role === user.role;
                const canClaim = !isOwn && roleMatch;
                return (
                  <div key={offer.id} className="rounded-lg border border-gray-200 p-4 hover:border-teal-300 transition-colors" data-testid={`open-offer-${offer.id}`}>
                    <ShiftLine schedule={offer.schedule} />
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                      <div className="text-sm text-gray-600">
                        <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5" /> Offered by {isOwn ? 'you' : (offer.offering_user?.name || 'a teammate')}</span>
                        {offer.reason && <span className="ml-2 italic text-gray-500">“{offer.reason}”</span>}
                      </div>
                      {isOwn ? (
                        <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" disabled={loading} onClick={() => handleWithdraw(offer.id)} data-testid={`withdraw-offer-btn-${offer.id}`}>
                          <Ban className="h-4 w-4 mr-1" /> Withdraw
                        </Button>
                      ) : canClaim ? (
                        <Button size="sm" className="bg-teal-600 hover:bg-teal-700" disabled={loading} onClick={() => handleClaim(offer.id)} data-testid={`claim-offer-btn-${offer.id}`}>
                          <Hand className="h-4 w-4 mr-1" /> Claim Shift
                        </Button>
                      ) : (
                        <Badge variant="outline" className="text-gray-500">Different role</Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* My offers & claims */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Clock className="h-5 w-5 text-gray-600" />
            <span>My Offers & Claims</span>
          </CardTitle>
          <CardDescription>Shifts you've offered up or claimed.</CardDescription>
        </CardHeader>
        <CardContent>
          {myOffers.length === 0 ? (
            <div className="text-center py-10 text-gray-500" data-testid="no-my-offers">
              <p>You haven't offered up or claimed any shifts yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {myOffers
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .map((offer) => {
                  const iAmOfferer = offer.offering_user_id === user.id;
                  const canWithdraw = iAmOfferer && ['open', 'claimed'].includes(offer.status);
                  return (
                    <div key={offer.id} className="rounded-lg border border-gray-200 bg-gray-50 p-4" data-testid={`my-offer-${offer.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <ShiftLine schedule={offer.schedule} />
                        {statusBadge(offer.status)}
                      </div>
                      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600">
                        <span>{iAmOfferer ? 'You offered this up' : `You claimed ${offer.offering_user?.name || 'a teammate'}'s shift`}</span>
                        {canWithdraw && (
                          <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" disabled={loading} onClick={() => handleWithdraw(offer.id)} data-testid={`my-withdraw-btn-${offer.id}`}>
                            <Ban className="h-4 w-4 mr-1" /> Withdraw
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Offer Up a Shift modal */}
      <Dialog open={showOfferModal} onOpenChange={setShowOfferModal}>
        <DialogContent className="max-w-md" data-testid="offer-shift-modal">
          <DialogHeader>
            <DialogTitle>Offer Up a Shift</DialogTitle>
            <DialogDescription>Pick one of your upcoming shifts to make available for a teammate to claim. No shift is traded back.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="mb-1.5 block">Shift to offer</Label>
              <Select value={selectedScheduleId} onValueChange={setSelectedScheduleId}>
                <SelectTrigger data-testid="offer-shift-select">
                  <SelectValue placeholder="Select a shift" />
                </SelectTrigger>
                <SelectContent>
                  {offerableSchedules.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500">No upcoming shifts available</div>
                  ) : (
                    offerableSchedules.map((s) => (
                      <SelectItem key={s.id} value={s.id} data-testid={`offer-shift-option-${s.id}`}>
                        {formatDate(s.date)} · {formatTime(s.start_time)}–{formatTime(s.end_time)} · {String(s.role || '').replace(/_/g, ' ')}{s.shift_type === 'on_call' ? ' (On-Call)' : ''}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">Reason (optional)</Label>
              <Textarea value={offerReason} onChange={(e) => setOfferReason(e.target.value)} placeholder="e.g. Out of town this weekend" data-testid="offer-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOfferModal(false)}>Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700" disabled={loading || !selectedScheduleId} onClick={handleCreateOffer} data-testid="confirm-offer-btn">
              Offer Up Shift
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OpenShifts;
